/* miniSubconcept.js — the clinical pathophysiology label shown INLINE (in brackets) to
   the right of each gamified mini name in the combat enemy rail, so a student sees what
   pathophys a mini actually represents while fighting it (Edward 2026-07-11).

   Display-only annotation — NO gameplay effect. Keyed by the mini's combat slug: the
   registry slug for the sub-mechanism pool minis, and the class slug for the Ten Shin
   (HTN) axis-minis (whose rail slug is their `class`). Names are the entity-first
   bare-noun canonical labels from the "Mini Subconcept Canonicalization" sheet. Bosses
   and generic/spawned enemies are intentionally absent, so they render no annotation. */

const BY_SLUG = {
  // Athero — lipid cascade
  chylo: 'Chylomicrons',
  veldra: 'VLDL',
  idol: 'IDL',
  eldra: 'LDL',
  'lipo-alpha': 'Lp(a)',
  // Anaphyl — Gell & Coombs hypersensitivity
  hyst: 'Type I (IgE)',
  lyse: 'Type II (cytotoxic)',
  plex: 'Type III (immune complex)',
  latanz: 'Type IV (delayed)',
  // Pestil — antimicrobial stewardship
  submic: 'Selection pressure',
  spectra: 'Dysbiosis',
  veilfilm: 'Biofilm',
  reservor: 'Latent reservoir',
  // Bradyx — Parkinson's
  bradykin: 'Bradykinesia',
  tremor: 'Resting tremor',
  dyskinex: 'Dyskinesia',
  hallux: 'Dopaminergic psychosis',
  // Convul — epilepsy
  idiopra: 'Seizure threshold',
  stata: 'Status epilepticus',
  toxica: 'Narrow therapeutic index',
  inductra: 'Enzyme induction',
  // Algo — pain
  nocir: 'Nociceptive pain',
  neuralg: 'Neuropathic pain',
  visceralg: 'Visceral pain',
  withdraw: 'Opioid withdrawal',
  // Ten Shin — resistant hypertension axes (rail slug === class)
  raas_warden: 'RAAS',
  calcium_gate: 'Arteriolar tone',
  volume_warden: 'Volume expansion',
  aldosterone_lock: 'Aldosterone',
  adrenergic_sentry: 'Sympathetic tone',
};

// Safety net keyed by the gamified display name (lowercased) — covers a surface that
// happens to pass the proper-name instead of the slug (notably the HTN axis-minis,
// whose slug is their class, not their name).
const BY_NAME = {
  // Prefer canonical Angio while retaining Angira for legacy persisted/anon sessions.
  angio: 'RAAS',
  angira: 'RAAS',
  calix: 'Arteriolar tone',
  natrix: 'Volume expansion',
  escara: 'Aldosterone',
  sympra: 'Sympathetic tone',
};

function norm(v) {
  return String(v == null ? '' : v).trim().toLowerCase();
}

/** Clinical subconcept label for a mini, or null if it is not a mapped mini.
 *  Resolves by combat slug first; then by name against the slug map (the pool
 *  minis' display name lowercased equals their slug, so this catches a surface
 *  that omits slug); then the HTN name aliases. */
export function miniSubconcept(slug, name) {
  return BY_SLUG[norm(slug)] || BY_SLUG[norm(name)] || BY_NAME[norm(name)] || null;
}

export default miniSubconcept;
